// src/components/Profile/Profile.jsx
import { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import HeaderOne from '../HeaderOne';
import Footer from '../BottomFooter';
import ProfileSection from './ProfileSection';
import AddressSection from './AddressSection';
import WishlistSection from './WishlistSection';
import OrdersSection from './OrdersSection';
import MenuCard from './MenuCard';
import OrderDetailsModal from './OrderDetailsModal';
import AddressModal from './AddressModal';
import ImagePreviewModal from './ImagePreviewModal';
import EditProfileModal from './EditProfileModal';
import ChangePasswordSection from './ChangePasswordSection';
import { formatDate, getImageUrl as createImageUrl } from '../../utils/profileHelpers';
import { splitOrdersByItems, mergeOrderItems, getCustomizationPreview } from '../../utils/orderHelpers';
import { getStatusStyle } from '../../utils/profileHelpers';

export default function Profile() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('profile');
  const [profileImage, setProfileImage] = useState(null);

  // Data states
  const [orders, setOrders] = useState([]);
  const [displayOrders, setDisplayOrders] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [wishlistItems, setWishlistItems] = useState([]);

  // UI states
  const [expandedOrders, setExpandedOrders] = useState({});
  const [showMoreOrders, setShowMoreOrders] = useState(false);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [expandedOrderItems, setExpandedOrderItems] = useState({});
  const [showEditModal, setShowEditModal] = useState(false);

  // Order item customization image editing
  const [editingOrderItemId, setEditingOrderItemId] = useState(null);
  const [orderItemNewImages, setOrderItemNewImages] = useState({});
  const [orderUploadingImages, setOrderUploadingImages] = useState({});

  // Cancel modal states
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancellationReason, setCancellationReason] = useState('');
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelError, setCancelError] = useState('');

  const navigate = useNavigate();
  const token = localStorage.getItem('access_token');
  const API_BASE_URL = process.env.REACT_APP_API_URL;
  const IMG_BASE_URL = process.env.REACT_APP_IMG_BASE_URL;

  const getImageUrl = (imageUrl) => createImageUrl(imageUrl, IMG_BASE_URL);

  useEffect(() => {
    if (!token) {
      navigate('/account');
      return;
    }
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      await Promise.all([fetchProfile(), fetchOrders(), fetchAddresses(), fetchWishlist()]);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  const fetchProfile = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/profile/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(res.data);
    } catch (err) {
      console.error(err);
      if (err.response?.status === 401) {
        localStorage.removeItem('access_token');
        navigate('/account');
      }
    }
  };

  const fetchOrders = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/orders/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(res.data);
      setDisplayOrders(splitOrdersByItems(res.data));
    } catch (err) {
      console.error('Error fetching orders:', err);
    }
  };

  const fetchAddresses = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/addresses/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAddresses(res.data);
    } catch (err) {
      console.error('Error fetching addresses:', err);
    }
  };

  const fetchWishlist = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/wishlist/my-wishlist/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWishlistItems(res.data.items || []);
    } catch (err) {
      console.error('Error fetching wishlist:', err);
    }
  };

  const handleCancelOrder = async () => {
    if (!cancellationReason.trim()) {
      setCancelError('Please provide a reason for cancellation');
      return;
    }

    setIsCancelling(true);
    setCancelError('');

    try {
      if (!token) {
        alert('Session expired. Please login again.');
        navigate('/login');
        return;
      }

      const response = await axios.post(
        `${API_BASE_URL}/orders/${selectedOrder.id}/cancel/`,
        { cancellation_reason: cancellationReason },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        setShowCancelModal(false);
        setCancellationReason('');
        await fetchOrders();
        setShowOrderModal(false);
        setSelectedOrder(null);
      }
    } catch (error) {
      console.error('Error cancelling order:', error);
      if (error.response?.data?.error) {
        setCancelError(error.response.data.error);
      } else if (error.response?.data?.cancellation_reason) {
        setCancelError(error.response.data.cancellation_reason[0]);
      } else {
        setCancelError('Failed to cancel order. Please try again.');
      }
    } finally {
      setIsCancelling(false);
    }
  };

  const toggleOrderExpand = (orderId) => {
    setExpandedOrders((prev) => ({
      ...prev,
      [orderId]: !prev[orderId]
    }));
  };

  const handleViewOrderDetails = (displayOrder) => {
    const completeOrder = mergeOrderItems(orders, displayOrder.id);
    setSelectedOrder(completeOrder);
    setShowOrderModal(true);
  };

  const handleViewImage = (imageUrl) => {
    setSelectedImage(getImageUrl(imageUrl));
    setShowImageModal(true);
  };

  const toggleOrderItemCustomizations = (itemId) => {
    setExpandedOrderItems((prev) => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const handleEditOrderItemImage = (order, item) => {
    if (order.status !== 'Pending') return;
    setEditingOrderItemId(item.id);
    setOrderItemNewImages((prev) => {
      const updated = { ...prev };
      delete updated[item.id];
      return updated;
    });
  };

  const handleOrderItemImageSave = async (order, item, file) => {
    try {
      setOrderUploadingImages((prev) => ({ ...prev, [item.id]: true }));

      if (!token) {
        alert('Session expired. Please login again.');
        return;
      }

      const formData = new FormData();
      formData.append('image', file);

      let customizationImageId = item.customization_image_id;
      let newImageUrl = null;

      if (!customizationImageId) {
        const createRes = await axios.post(`${API_BASE_URL}/customization-images/`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });

        if (createRes.data?.success) {
          const imageData = createRes.data.data || createRes.data;
          customizationImageId = imageData.image_id || imageData.id;
          newImageUrl = imageData.image || imageData.image_url;

          if (!customizationImageId) {
            throw new Error('No customization image ID returned from server');
          }

          const patchPayload = { customization_image_id: parseInt(customizationImageId) };
          const patchRes = await axios.patch(
            `${API_BASE_URL}/order-items/${item.id}/update_customization_image/`,
            patchPayload,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            }
          );

          if (!patchRes.data || patchRes.data.error) {
            throw new Error('Failed to link image to order item');
          }
        } else {
          alert('Failed to upload image. Please try again.');
          return;
        }
      } else {
        const updateRes = await axios.patch(
          `${API_BASE_URL}/customization-images/${customizationImageId}/`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );

        if (updateRes.data?.success || updateRes.data?.id) {
          const imageData = updateRes.data.data || updateRes.data;
          newImageUrl = imageData.image || imageData.image_url;
        } else {
          alert('Failed to update image. Please try again.');
          return;
        }
      }

      if (newImageUrl) {
        const cacheBustedUrl = `${newImageUrl}?t=${Date.now()}`;

        setOrders((prev) =>
          prev.map((ord) =>
            ord.id !== order.id
              ? ord
              : {
                ...ord,
                items: ord.items.map((it) =>
                  it.id === item.id
                    ? {
                      ...it,
                      customization_image_url: cacheBustedUrl,
                      customization_image_id: customizationImageId
                    }
                    : it
                )
              }
          )
        );

        if (selectedOrder && selectedOrder.id === order.id) {
          setSelectedOrder((prev) => ({
            ...prev,
            items: prev.items.map((it) =>
              it.id === item.id
                ? {
                  ...it,
                  customization_image_url: cacheBustedUrl,
                  customization_image_id: customizationImageId
                }
                : it
            )
          }));
        }

        alert('✅ Image saved successfully!');
      } else {
        throw new Error('No image URL returned from server');
      }

      setEditingOrderItemId(null);
      setOrderItemNewImages((prev) => {
        const updated = { ...prev };
        delete updated[item.id];
        return updated;
      });
    } catch (err) {
      console.error('Error updating order item image:', err);
      alert(`❌ Failed to update image: ${err.response?.data?.detail || err.message || 'Please try again.'}`);
    } finally {
      setOrderUploadingImages((prev) => ({ ...prev, [item.id]: false }));
    }
  };

  const handleAddNewAddress = () => {
    setEditingAddress(null);
    setShowAddressModal(true);
  };

  const handleEditAddress = (address) => {
    setEditingAddress(address);
    setShowAddressModal(true);
  };

  const handleDeleteAddress = async (addressId) => {
    if (!window.confirm('Are you sure you want to delete this address?')) return;

    try {
      await axios.delete(`${API_BASE_URL}/addresses/${addressId}/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAddresses(addresses.filter((addr) => addr.id !== addressId));
      alert('✅ Address deleted successfully');
    } catch (err) {
      console.error('Error deleting address:', err);
      alert('❌ Failed to delete address');
    }
  };

  const handleSaveAddress = async (addressData) => {
    try {
      if (editingAddress) {
        const res = await axios.put(
          `${API_BASE_URL}/addresses/${editingAddress.id}/`,
          {
            address_data: addressData,
            is_default: editingAddress.is_default
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        setAddresses(addresses.map((addr) => (addr.id === editingAddress.id ? res.data : addr)));
        alert('✅ Address updated successfully!');
      } else {
        const res = await axios.post(
          `${API_BASE_URL}/addresses/`,
          {
            address_data: addressData,
            is_default: addresses.length === 0
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        setAddresses([...addresses, res.data]);
        alert('✅ Address added successfully!');
      }
      setShowAddressModal(false);
      setEditingAddress(null);
    } catch (err) {
      console.error('Error saving address:', err);
      alert('❌ Failed to save address: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleEditProfileSave = async (profileData) => {
    try {
      const res = await axios.patch(`${API_BASE_URL}/profile/`, profileData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      setUser(res.data);
      alert('✅ Profile updated successfully!');
      setShowEditModal(false);
    } catch (err) {
      console.error('Error updating profile:', err);
      const errorMsg = err.response?.data ? Object.values(err.response.data).flat().join(', ') : err.message;
      alert('❌ Failed to update profile: ' + errorMsg);
      throw err;
    }
  };

  const renderSectionContent = () => {
    switch (activeSection) {
      case 'profile':
        return <ProfileSection user={user} profileImage={profileImage} onEditProfile={() => setShowEditModal(true)} />;
      case 'address':
        return (
          <AddressSection
            addresses={addresses}
            onAddNew={handleAddNewAddress}
            onEdit={handleEditAddress}
            onDelete={handleDeleteAddress}
          />
        );
      case 'wishlist':
        return <WishlistSection wishlistItems={wishlistItems} getImageUrl={getImageUrl} />;
      case 'password':
        return <ChangePasswordSection />;
      default:
        return <ProfileSection user={user} profileImage={profileImage} onEditProfile={() => setShowEditModal(true)} />;
    }
  };

  if (loading) {
    return (
      <>
        <HeaderOne />
        <div className="profile-wrapper">
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading your profile...</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (!user) {
    return (
      <>
        <HeaderOne />
        <div className="profile-wrapper">
          <div className="error-container">
            <div className="alert alert-danger">Unable to load profile</div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <HeaderOne />
      <div className="profile-wrapper">
        <div className="profile-dashboard">
          <div className="dashboard-header">
            <div className="header-content">
              <div className="header-left">
                <h1 className="dashboard-title">My Profile Dashboard</h1>
                <p className="dashboard-subtitle">Welcome to your personal portal</p>
              </div>
              <div className="header-right">
                <div className="user-greeting">
                  <span className="greeting-text">Hello {user.name}</span>
                  <i className="ph ph-caret-down"></i>
                </div>
              </div>
            </div>
          </div>

          <div className="dashboard-content">
            <div className="content-wrapper">
              <div className="content-grid">
                {renderSectionContent()}
                <MenuCard activeSection={activeSection} onSectionChange={setActiveSection} />
                <OrdersSection
                  displayOrders={displayOrders}
                  expandedOrders={expandedOrders}
                  showMoreOrders={showMoreOrders}
                  onToggleExpand={toggleOrderExpand}
                  onViewDetails={handleViewOrderDetails}
                  onToggleShowMore={() => setShowMoreOrders(!showMoreOrders)}
                  formatDate={formatDate}
                  getStatusStyle={getStatusStyle}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showOrderModal && selectedOrder && (
        <OrderDetailsModal
          order={selectedOrder}
          onClose={() => {
            setShowOrderModal(false);
            setSelectedOrder(null);
          }}
          onViewImage={handleViewImage}
          onEditItemImage={handleEditOrderItemImage}
          editingOrderItemId={editingOrderItemId}
          orderItemNewImages={orderItemNewImages}
          setOrderItemNewImages={setOrderItemNewImages}
          orderUploadingImages={orderUploadingImages}
          setOrderUploadingImages={setOrderUploadingImages}
          expandedItems={expandedOrderItems}
          toggleExpand={toggleOrderItemCustomizations}
          getCustomizationPreview={getCustomizationPreview}
          getImageUrl={getImageUrl}
          handleOrderItemImageSave={handleOrderItemImageSave}
          setEditingOrderItemId={setEditingOrderItemId}
          showCancelModal={showCancelModal}
          setShowCancelModal={setShowCancelModal}
          cancellationReason={cancellationReason}
          setCancellationReason={setCancellationReason}
          isCancelling={isCancelling}
          cancelError={cancelError}
          setCancelError={setCancelError}
          handleCancelOrder={handleCancelOrder}
          token={token}
        />
      )}

      <ImagePreviewModal imageUrl={selectedImage} isOpen={showImageModal} onClose={() => setShowImageModal(false)} />

      {showAddressModal && (
        <AddressModal
          address={editingAddress}
          onSave={handleSaveAddress}
          onClose={() => {
            setShowAddressModal(false);
            setEditingAddress(null);
          }}
        />
      )}

      {showEditModal && (
        <EditProfileModal user={user} onSave={handleEditProfileSave} onClose={() => setShowEditModal(false)} />
      )}

      <Footer />
    </>
  );
}